import { sound } from '@pixi/sound';
import SoundGroup from './SoundGroup';

/**
 * Класс упарвления звуками в игре
 */
export default new class SoundManager {
  constructor() {
    this.sound = null;
    this._soundsConfig = {};
    this._groups = {};

    this._blurMutedState = {};

    this._masterVolume = 1;

    this.addListeners();

    this._getSound();
    this.addNewSoundGroup('default');
  }

  addListeners() {
    window.addEventListener('visibilitychange', this._onVisibilityChange.bind(this));
  }

  _onVisibilityChange() {
    if (document.visibilityState === 'visible') {
      this.unmuteAll();
      const contextsForResume = [];
        Object.values(this.groups).map(group => {
          group.sounds.forEach(sound => {
            const context = sound.sound.media._source.context
            if (!contextsForResume.includes(context)) contextsForResume.push(context);
          });
        });

        contextsForResume.forEach((context) => this.__resumeAudioContext(context));

    } else {
      this.muteAll();
    }
  }

  __resumeAudioContext(context, iterationCount = 0) {
    if (iterationCount > 20) return;
      context.suspend()
        .then(() => {
          return context.resume()
        })
        .catch(() => {
          requestAnimationFrame(() => {
            iterationCount++;
            this.__resumeAudioContext(context, iterationCount);
          });
        });
  }

  getSoundConfigData(alias) {
    return this.soundsConfig[alias] || {};
  }

  unmuteAll() {
    Object.values(this.groups).forEach(group => {
      group.forceMuted = false;
    });
  }

  muteAll() {
    Object.values(this.groups).forEach(group => {
      group.forceMuted = true;
    });
  }

  /**
   * Получить текущий звуковой файл
   * @private
   */
  _getSound() {
    this.sound = sound;
    this.sound.disableAutoPause = true;
  }

  /**
   *   * Play sound by alias
   *    * @param alias sound's name
   *    * @param volume volume level
   *    * @param loop loop mode
   *    * @param groupName group's name
   *    */
  play(alias, volume = 1, loop = false, groupName = 'default', start, end, speed) {
    let source = this.getSound(alias);

    if (!source) {
      console.warn(`Sound Manager: sound ${alias} not found`);
      return null;
    }

    if (!source.isLoaded) {
      console.warn(`Sound Manager: sound ${alias} not loaded yet, try again`);
      return null;
    }

    let config = this.getSoundConfigData(alias);

    const playOptions = {
      loop: config.hasOwnProperty('loop') ? config.loop : loop,
      volume: this._masterVolume * config.hasOwnProperty('volume') ? config.volume : volume,
      start: config.hasOwnProperty('start') ? config.start : (start || 0),
      end: config.hasOwnProperty('end') ? config.end : (typeof end === 'number' ? end : source.duration),
      speed: config.hasOwnProperty('speed') ? config.speed : (typeof speed === 'number' ? speed : 1),
    };

    let instance = source.play(playOptions);
    source.baseVolume = volume;

    let gro = config.hasOwnProperty('group') ? config.group : groupName;

    this.addSoundToGroup(source, alias, gro);
    return instance;
  }

  /**
   * Play delayed sound by alias
   * @param alias sound's name
   * @param options play options
   * @param skipNotReady set when you don't have to await
   */
  async playDelayed(alias, options, skipNotReady) {
    const { volume, loop, groupName, start, end } = options;
    const source = this.getSound(alias);

    if (!source) {
      const promises = [];

      let wasSkipped = false;
      if (skipNotReady) {
        const skipOnNextFramePromise = new Promise((resolve) => requestAnimationFrame(() => {
          wasSkipped = true;
          resolve();
        }));
        promises.push(skipOnNextFramePromise);
      }

      const loadPromise = PIXI.Assets.load(alias);
      promises.push(loadPromise);

      await Promise.race(promises); // wait for load or skip

      if (wasSkipped) return;
    }
    return this.play(alias, volume, loop, groupName, start, end);
  }

  /**
   * Остановить звук по имени
   * @param alias имя звука
   */
  stop(alias) {
    let sound = this.getSound(alias);
    if (sound) {
      let instance = sound.stop();
      return instance;
    }
  }

  /**
   *
   * @param alias
   */
  pause(alias) {
    let sound = this.getSound(alias);
    if (sound.isPlaying) {
      sound.pause(alias);
    }
  }

  /**
   *
   * @param alias
   */
  resume(alias) {
    let sound = this.getSound(alias);
    if (sound && !sound.isPlaying) {
      sound.resume(alias);
    }
  }

  /**
   *
   * @param groupName
   * @param value
   */
  muteGroup(groupName, value) {
    let group = this.getGroup(groupName);
    if (group) {
      if (value === undefined) {
        group.switchMuted();
      } else {
        group.muted = value;
      }
    }
  }

  /**
   *
   * @param groupName
   * @param value
   */
  volumeGroup(groupName, value) {
    let group = this.getGroup(groupName);
    if (group) {
      group.volume = value;
    }
  }

  /**
   *
   * @param alias
   * @returns {*}
   */
  getSound(alias) {
    return PIXI.Assets.cache.has(alias) ? PIXI.Assets.get(alias) : null;
  }

  /**
   *
   * @param groupName
   */
  addNewSoundGroup(groupName) {
    if (!this.groups.hasOwnProperty(groupName)) {
      this._groups[groupName] = new SoundGroup(groupName);
    }
  }

  /**
   *
   * @param sound
   * @param alias
   * @param groupName
   */
  addSoundToGroup(sound, alias, groupName) {
    let group = this.getGroup(groupName);
    group.addSound(sound, alias);
  }

  getGroup(groupName) {
    if (!this.groups.hasOwnProperty(groupName)) {
      this.addNewSoundGroup(groupName);
    }
    return this.groups[groupName];
  }

  /**
   *
   * @returns {*}
   */
  get groups() {
    return this._groups;
  }

  set soundsConfig(value) {
    this._soundsConfig = value;
  }

  get soundsConfig() {
    return this._soundsConfig;
  }

  set masterVolume(value) {
    this._masterVolume = value;
  }

  get masterVolume() {
    return this._masterVolume;
  }
}
